import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { KIND } from "baseui/button";
import { ConfirmDialog } from "components/confirm-dialog";
import { Content } from "components/content";
import { FormRenderer } from "components/form-renderer";
import { Header } from "components/header";
import { NoPermissionsRedirect } from "components/no-permissions-redirect";
import { SlideVariant } from "components/slide-variant-selector/slide-variant-selector";
import { useAuth } from "contexts/auth-context";
import { useDictionaries } from "contexts/dictionaries-context";
import { useLoading } from "contexts/loading-context";
import { useProject } from "contexts/project-context";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory, useParams } from "react-router-dom";
import { DeviceFloppy, FileOff } from "tabler-icons-react";
import { PERMISSIONS } from "utils/permissions";

import { Slide } from "../slides";
import { SLIDES_FIELDS, SlidesFormInputs } from "../slides.form";
import { SLIDES_SHOW, SLIDES_UPDATE } from "../slides.gql";

export default function SlidesUpdate(): React.ReactElement {
  const [isCancelConfirmDialogOpen, setIsCancelConfirmDialogOpen] = useState(
    false
  );
  const { enqueueSnackbar } = useSnackbar();
  const { setIsFetching, isLoading, setIsLoading } = useLoading();
  const { checkPermission } = useAuth();
  const history = useHistory();
  const { id } = useParams<{ id?: string }>();
  const { activeProject, activeProjectId } = useProject();
  const { findValue } = useDictionaries();
  const [originalProjectId, setOriginalProjectId] = useState<number>();

  const {
    control,
    formState: { errors, isDirty, ...formState },
    handleSubmit,
    setValue,
    reset,
    unregister,
    watch,
    ...methods
  } = useForm<SlidesFormInputs>();

  const { refetch, data, error: queryError } = useQuery(SLIDES_SHOW, {
    variables: { id: id ? parseInt(id) : null },
  });
  const slide: Slide = data?.slide;

  useEffect(() => {
    setIsFetching(true);
    if (data?.slide) refetch();
  }, []);

  useEffect(() => {
    if (queryError?.graphQLErrors)
      enqueueSnackbar({
        message: (queryError as ApolloError).graphQLErrors.map(
          ({ message }) => message
        )[0],
        variant: "error",
      });
  }, [queryError]);

  useEffect(() => {
    async function setFile() {
      const response = await fetch(slide?.imageUrl);
      const data = await response.blob();

      const extension = /\.(jpe?g|png|gif|svg)$/gi.exec(slide?.imageUrl)?.[1];

      const metadata = {
        type: `image/${extension === "svg" ? "svg+xml" : "png"}`,
      };

      const file = new File([data], `${slide?.id}.${extension}`, metadata);

      setValue("image", file);
      setIsFetching(false);
    }

    if (data?.slide) {
      setOriginalProjectId(slide?.project?.id);

      setValue("project", [{ ...slide?.project, id: slide?.project?.id }]);
      setValue("language", [{ id: slide?.language }]);
      setValue("title", slide?.title);
      setValue("isVisible", slide?.isVisible);
      setValue("lead", slide?.lead);
      !!slide?.position && setValue("position", slide?.position);

      if (slide?.imageUrl) {
        setFile();
      } else {
        setIsFetching(false);
      }

      if (slide?.article) {
        setValue("slideVariant", {
          type: SlideVariant.Article,
          value: slide?.article?.id,
          buttonLabel: "",
        });
      }
      if (slide?.post) {
        setValue("slideVariant", {
          type: SlideVariant.Post,
          value: slide?.post?.id,
          buttonLabel: "",
        });
      }
      if (slide?.staticPage) {
        setValue("slideVariant", {
          type: SlideVariant.StaticPage,
          value: slide?.staticPage?.id,
          buttonLabel: "",
        });
      }
      if (slide?.url) {
        setValue("slideVariant", {
          type: SlideVariant.Custom,
          value: slide?.url,
          buttonLabel: slide?.buttonLabel,
        });
      }
    }
  }, [data]);

  useEffect(() => {
    !activeProject &&
      setValue("project", [{ ...slide?.project, id: slide?.project?.id }]);
  }, [activeProject]);

  const watchProject = watch("project");

  useEffect(() => {
    if (
      originalProjectId === watchProject?.[0]?.id ||
      originalProjectId === activeProjectId
    )
      setValue("language", [
        {
          id: slide?.language,
          label: (findValue(slide?.language as string) as unknown) as string,
        },
      ]);
    else setValue("language", []);
  }, [watchProject, activeProject]);

  useEffect(() => {
    setIsFetching(true);
    if (data?.slide) refetch();
  }, []);

  const [updateSlide, { error }] = useMutation(SLIDES_UPDATE);

  const onSubmit = async ({
    project,
    language,
    isVisible,
    slideVariant,
    title,
    lead,
    image,
    position,
  }: SlidesFormInputs): Promise<void> => {
    setIsLoading(true);

    try {
      await updateSlide({
        variables: {
          slideUpdateInput: {
            id: id ? parseInt(id) : null,
            projectId: activeProjectId || project?.[0]?.id,
            language: language?.[0]?.id,
            isVisible: !!isVisible,
            ...(slideVariant?.type === SlideVariant.Article && {
              articleId: parseInt(slideVariant.value as string),
              postId: null,
              staticPageId: null,
              url: null,
              buttonLabel: null,
            }),
            ...(slideVariant?.type === SlideVariant.Custom && {
              url: slideVariant.value,
              buttonLabel: slideVariant.buttonLabel,
              articleId: null,
              postId: null,
              staticPageId: null,
            }),
            ...(slideVariant?.type === SlideVariant.Post && {
              postId: parseInt(slideVariant.value as string),
              url: null,
              buttonLabel: null,
              articleId: null,
              staticPageId: null,
            }),
            ...(slideVariant?.type === SlideVariant.StaticPage && {
              staticPageId: parseInt(slideVariant.value as string),
              url: null,
              buttonLabel: null,
              postId: null,
              articleId: null,
            }),
            title,
            lead,
            image,
            position: parseInt(position as string),
          },
        },
      });
      enqueueSnackbar({
        message: "Zapisano pomyślnie",
        variant: "success",
      });
      history.push(`/slides/${id}`);
    } catch (error: unknown) {
      enqueueSnackbar({
        message: (error as ApolloError).graphQLErrors.map(
          ({ message }) => message
        )[0],
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!checkPermission(PERMISSIONS.slide.update))
    return <NoPermissionsRedirect />;

  return (
    <article>
      <Header
        title={slide?.title}
        labels={["Slajdy", "Edytowanie"]}
        goBackOption
        buttons={[
          {
            label: "Anuluj",
            kind: KIND.secondary,
            startEnhancer: <FileOff size={18} />,
            disabled: isLoading,
            onClick: () =>
              isDirty ? setIsCancelConfirmDialogOpen(true) : history.goBack(),
          },
          {
            label: "Zapisz",
            kind: KIND.primary,
            startEnhancer: <DeviceFloppy size={18} />,
            onClick: handleSubmit(onSubmit),
            disabled: isLoading,
            isLoading: isLoading,
            type: "submit",
            formId: "updateSlide",
          },
        ]}
      />
      <Content>
        <FormProvider<SlidesFormInputs>
          control={control}
          formState={{ errors, isDirty, ...formState }}
          handleSubmit={handleSubmit}
          reset={reset}
          setValue={setValue}
          unregister={unregister}
          watch={watch}
          {...methods}
        >
          <FormRenderer<SlidesFormInputs>
            id="updateSlide"
            onSubmit={handleSubmit(onSubmit)}
            type="update"
            isLoading={isLoading}
            error={error}
            fields={SLIDES_FIELDS}
          />
        </FormProvider>
        <ConfirmDialog
          isOpen={isCancelConfirmDialogOpen}
          label="Anulowanie edycji slajdu"
          close={() => setIsCancelConfirmDialogOpen(false)}
          confirm={() => history.goBack()}
        />
      </Content>
    </article>
  );
}
